import React, { useEffect, useState } from 'react';
import { Package, Search, Loader2, Plus, Minus, Pencil, Trash2, AlertTriangle, ShoppingCart, History, Download } from 'lucide-react';
import { toast } from 'sonner';
import { MATERIAL_STATUS_META } from '../mock/data';
import { PageHeader, StatCard, StatusBadge, Modal, Field } from '../components/common';
import { useRealtime } from '../realtime/RealtimeContext';
import { exportCSV } from '../utils/exportCSV';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import UseMaterialModal from '../components/UseMaterialModal';

const CATEGORIES = ['Raw Metal', 'Polymer', 'Fasteners', 'Consumable', 'Tooling', 'General'];
const EMPTY = { code: '', name: '', category: 'Raw Metal', stock: 0, unit: 'kg', reorder: 100, supplier: '' };

export default function Materials() {
  const { t } = useI18n();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [usageModal, setUsageModal] = useState(null);
  const [usage, setUsage] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);
  const [useTarget, setUseTarget] = useState(null);
  const { user } = useAuth();
  const isManager = user?.role === 'ADMIN' || user?.role === 'SUPERVISOR';

  const openUsage = async (m) => {
    setUsageModal(m); setUsage([]); setUsageLoading(true);
    try { setUsage(await api.materialUsage(m.code)); }
    catch (e) { setUsage([]); }
    finally { setUsageLoading(false); }
  };

  const exportUsage = () => {
    if (!usageModal || usage.length === 0) return;
    exportCSV(`kmt_usage_${usageModal.code}.csv`, [
      { label: 'Date/Time', get: (u) => new Date(u.ts).toLocaleString() },
      { label: 'Material Code', get: (u) => u.code },
      { label: 'Material', get: (u) => u.name },
      { label: 'Change', get: (u) => `${u.delta > 0 ? '+' : ''}${u.delta}` },
      { label: 'Unit', get: (u) => u.unit },
      { label: 'Balance', get: (u) => u.stock },
      { label: 'Action', get: (u) => u.note },
      { label: 'Machine', get: (u) => u.machine || '—' },
      { label: 'By', get: (u) => u.actor },
    ], usage);
    toast.success(`Usage history exported (${usage.length} rows)`);
  };

  const load = () => api.listMaterials().then(setMaterials).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  useRealtime(() => load());

  const rows = materials.filter((m) => !query || m.name.toLowerCase().includes(query.toLowerCase()) || m.code.toLowerCase().includes(query.toLowerCase()));
  const critical = materials.filter((m) => m.status === 'CRITICAL');
  const low = materials.filter((m) => m.status === 'LOW');

  const openCreate = () => { setForm(EMPTY); setModal({ mode: 'create' }); };
  const openEdit = (m) => { setForm({ ...m }); setModal({ mode: 'edit' }); };
  const num = (v) => (v === '' ? '' : Number(v));

  const save = async () => {
    setSaving(true);
    try {
      if (modal.mode === 'create') {
        const created = await api.createMaterial(form);
        setMaterials((p) => [...p, created]);
        toast.success(`${created.code} added`);
      } else {
        const upd = await api.updateMaterial(form.code, form);
        setMaterials((p) => p.map((x) => (x.code === upd.code ? upd : x)));
        if (upd.status === 'CRITICAL') toast.error(`${upd.code} is now CRITICAL — restock needed`);
        else if (upd.status === 'LOW') toast.warning?.(`${upd.code} is LOW`) || toast(`${upd.code} is LOW`);
        else toast.success(`${upd.code} updated`);
      }
      setModal(null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (m) => {
    setMaterials((p) => p.filter((x) => x.code !== m.code));
    try { await api.deleteMaterial(m.code); toast.success(`${m.code} deleted`); }
    catch (e) { toast.error('Delete failed'); load(); }
  };

  const reorder = async (m) => {
    try {
      const draft = await api.reorderMaterial(m.code);
      toast.success(`Draft delivery created for ${m.name}`, { description: `${draft.qty} · see Material Schedule` });
    } catch (e) { toast.error('Reorder failed'); }
  };

  return (
    <div className="kmt-fade-in">
      <PageHeader icon={Package} title="Materials Inventory" subtitle={`${materials.length} SKUs tracked · auto stock alerts`}
        right={isManager && <button className="kmt-btn-gold flex items-center gap-2" onClick={openCreate} data-testid="material-add-btn"><Plus size={16} />Add Material</button>} />
      <div className="p-4 md:p-8 space-y-6">
        {!isManager && (
          <div className="text-sm" style={{ color: 'var(--kmt-muted)' }} data-testid="material-operator-hint">{t('mat.opHint')}</div>
        )}
        {(critical.length > 0 || low.length > 0) && (
          <div className="kmt-panel p-4 flex items-center gap-3" style={{ borderColor: 'rgba(240,82,82,0.4)' }}>
            <AlertTriangle size={18} style={{ color: 'var(--kmt-red)' }} />
            <span className="text-sm">
              <b style={{ color: 'var(--kmt-red)' }}>{critical.length} critical</b> and <b style={{ color: 'var(--kmt-amber)' }}>{low.length} low</b> stock items need restocking.
            </span>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total SKUs" value={materials.length} />
          <StatCard label="Low Stock" value={low.length} accent="var(--kmt-amber)" />
          <StatCard label="Critical" value={critical.length} accent="var(--kmt-red)" />
          <StatCard label="Suppliers" value={new Set(materials.map((m) => m.supplier)).size} accent="var(--kmt-gold)" />
        </div>
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--kmt-muted-2)' }} />
          <input className="kmt-input" style={{ paddingLeft: 40 }} placeholder="Search materials…" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-24" style={{ color: 'var(--kmt-muted)' }}><Loader2 className="animate-spin" size={28} /></div>
        ) : (
          <div className="kmt-panel overflow-x-auto">
            <table className="w-full kmt-table">
              <thead><tr>
                <th className="kmt-th">Code</th><th className="kmt-th">Material</th><th className="kmt-th">Category</th><th className="kmt-th">Stock</th><th className="kmt-th">Reorder Pt</th><th className="kmt-th">Supplier</th><th className="kmt-th">Status</th><th className="kmt-th">Actions</th>
              </tr></thead>
              <tbody>
                {rows.map((m) => (
                  <tr key={m.code}>
                    <td className="kmt-td font-mono-k" data-label="Code" style={{ color: 'var(--kmt-gold)' }}>{m.code}</td>
                    <td className="kmt-td" data-label="Material">{m.name}</td>
                    <td className="kmt-td" data-label="Category" style={{ color: 'var(--kmt-muted)' }}>{m.category}</td>
                    <td className="kmt-td font-mono-k" data-label="Stock">{m.stock.toLocaleString()} {m.unit}</td>
                    <td className="kmt-td font-mono-k" data-label="Reorder Pt" style={{ color: 'var(--kmt-muted)' }}>{m.reorder.toLocaleString()}</td>
                    <td className="kmt-td" data-label="Supplier">{m.supplier}</td>
                    <td className="kmt-td" data-label="Status"><StatusBadge meta={MATERIAL_STATUS_META[m.status] || MATERIAL_STATUS_META.OK} /></td>
                    <td className="kmt-td" data-label="Actions">
                      <div className="flex gap-2 items-center">
                        <button onClick={() => setUseTarget(m)} disabled={(m.stock || 0) <= 0} data-testid={`material-use-${m.code}`}
                          className="kmt-btn-ghost text-xs flex items-center gap-1.5" style={{ padding: '5px 9px', color: 'var(--kmt-red)', opacity: (m.stock || 0) <= 0 ? 0.4 : 1 }}>
                          <Minus size={13} />{t('mat.use')}
                        </button>
                        {isManager && (m.status === 'CRITICAL' || m.status === 'LOW') && (
                          <button onClick={() => reorder(m)} title="Create draft delivery"
                            className="kmt-btn-ghost text-xs flex items-center gap-1.5" style={{ padding: '5px 9px', color: 'var(--kmt-gold)' }}>
                            <ShoppingCart size={13} />Reorder
                          </button>
                        )}
                        <button onClick={() => openUsage(m)} title="Usage history" data-testid={`material-history-${m.code}`} style={{ color: 'var(--kmt-muted)' }} className="hover:text-[var(--kmt-blue)]"><History size={15} /></button>
                        {isManager && <button onClick={() => openEdit(m)} data-testid={`material-edit-${m.code}`} style={{ color: 'var(--kmt-muted)' }} className="hover:text-[var(--kmt-gold)]"><Pencil size={15} /></button>}
                        {isManager && <button onClick={() => remove(m)} data-testid={`material-delete-${m.code}`} style={{ color: 'var(--kmt-muted)' }} className="hover:text-[var(--kmt-red)]"><Trash2 size={15} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={!!modal} onClose={() => setModal(null)}
        title={modal?.mode === 'create' ? 'Add Material' : `Edit ${form.code}`}
        footer={<>
          <button className="kmt-btn-ghost" onClick={() => setModal(null)}>Cancel</button>
          <button className="kmt-btn-gold" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
        </>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Code">
            <input className="kmt-input" value={form.code} disabled={modal?.mode === 'edit'} placeholder="MAT-XXX-08" onChange={(e) => setForm({ ...form, code: e.target.value })} />
          </Field>
          <Field label="Name">
            <input className="kmt-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Category">
            <input className="kmt-input" list="mat-cats" value={form.category}
              placeholder="Type or pick a custom category"
              onChange={(e) => setForm({ ...form, category: e.target.value })} />
            <datalist id="mat-cats">{CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
          </Field>
          <Field label="Supplier">
            <input className="kmt-input" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
          </Field>
          <Field label="Stock">
            <input className="kmt-input" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: num(e.target.value) })} />
          </Field>
          <Field label="Unit">
            <input className="kmt-input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
          </Field>
          <Field label="Reorder Point">
            <input className="kmt-input" type="number" value={form.reorder} onChange={(e) => setForm({ ...form, reorder: num(e.target.value) })} />
          </Field>
        </div>
        <p className="text-xs mt-4" style={{ color: 'var(--kmt-muted)' }}>Status auto-flags: CRITICAL below 50% of reorder point, LOW below reorder point.</p>
      </Modal>

      <UseMaterialModal material={useTarget} onClose={() => setUseTarget(null)}
        onDone={(upd) => setMaterials((p) => p.map((x) => (x.code === upd.code ? upd : x)))} />

      <Modal open={!!usageModal} onClose={() => setUsageModal(null)}
        title={usageModal ? `Usage History · ${usageModal.name}` : 'Usage History'}
        footer={<>
          <button className="kmt-btn-ghost flex items-center gap-2" onClick={exportUsage} disabled={usage.length === 0} data-testid="material-usage-export" style={{ opacity: usage.length === 0 ? 0.5 : 1 }}><Download size={15} />Export CSV</button>
          <button className="kmt-btn-gold" onClick={() => setUsageModal(null)}>Close</button>
        </>}>
        {usageLoading ? (
          <div className="flex items-center justify-center py-10" style={{ color: 'var(--kmt-muted)' }}><Loader2 className="animate-spin" size={24} /></div>
        ) : usage.length === 0 ? (
          <div className="text-sm text-center py-10" style={{ color: 'var(--kmt-muted)' }} data-testid="material-usage-empty">No consumption or restock activity logged yet for this material.</div>
        ) : (
          <div className="max-h-96 overflow-y-auto -mx-1" data-testid="material-usage-list">
            {usage.map((u) => (
              <div key={u.id} className="flex items-center justify-between px-3 py-2.5 text-sm" style={{ borderBottom: '1px solid var(--kmt-border)' }} data-testid={`material-usage-row-${u.id}`}>
                <div className="min-w-0">
                  <span className="font-mono-k font-bold" style={{ color: u.delta < 0 ? 'var(--kmt-red)' : 'var(--kmt-green)' }}>{u.delta > 0 ? '+' : ''}{u.delta} {u.unit}</span>
                  <span className="ml-2" style={{ color: 'var(--kmt-muted)' }}>{u.note} · balance {u.stock}{u.machine ? ` · ${u.machine}` : ''}</span>
                </div>
                <div className="text-right shrink-0 ml-3 text-xs">
                  <div style={{ color: 'var(--kmt-text)' }}>{u.actor}</div>
                  <div style={{ color: 'var(--kmt-muted-2)' }}>{new Date(u.ts).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
